/* eslint-disable react-hooks/exhaustive-deps */
import ConfirmationDialog from "components/dialog/confirmation";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import LuckyDrawListingPanelComponent from "./luckyDrawListingPanel.component";
import { addAlert, selectLoading } from "modules/notification";
import {AccessRights} from "lib/constants/accessRights";
import { generatePaginationOptions } from "lib/helper";
import { getApiLang, getLang } from "app/feature/constants";
import { getAllProducts } from "../../../../product-mgmt/redux/action/products.action";
import luckyDrawV2Api from "app/api/luckyDrawV2";
import LiveDrawSetupDialog from "../../dialog/liveDrawSetupDialog";
import { progressStatus } from "lib/constants/luckyDrawProgress";
import {
  INSTANT_WIN_CREATE,
  LUCKY_DRAW_CREATE,
  ROOT,
} from "modules/lucky-draw-v2/config/lucky-draw-v2.route";
import { useHistory } from "react-router-dom";
import LuckyDrawBindingDialog from "../../dialog/luckyDrawBindingDialog";
import { hideBackdrop, showBackdrop } from "modules/backdrop";
import GeneralDialog from "components/dialog/generalDialog";
import AlertIcon from "assets/svg/alert.svg";
import {
  fetchTraditionalRaffleList,
  fetchInstantWinList,
  setListingActiveTab,
  setListingFilters,
  clearListingFilters,
  setListingPage,
  setListingRowsPerPage,
  resetListingState,
} from "../../../redux";
import { canAccess } from "../../../../profile/redux/selector/profile.selector";
import { useLuckyDraw } from "../../../context/luckyDraw.context";

const LuckyDrawListingPanelContainer = forwardRef((props, ref) => {
  const { onTabChange } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const hasLuckyDrawEditAccessRight = useSelector((state) =>
    canAccess(state, AccessRights.CampaignV2.LUCKY_DRAW_LIST_WRITE));

  const productDropdown = useSelector((state) => state.products.allProducts);
  const isFetchingProduct = useSelector((state) =>
    selectLoading(state, getAllProducts.typePrefix)
  );
  const lang = useSelector((state) => state.constant.languages);
  const language = useSelector((state) => state.profile.language);
  const accessToken = useSelector((state) => state.session.accessToken);

  // Redux state for listing
  const {
    traditionalRaffleList,
    traditionalRaffleTotalRecords,
    traditionalRaffleFilteredRecords,
    isFetchingTraditionalRaffle,
    traditionalRaffleError,
    instantWinList,
    instantWinTotalRecords,
    instantWinFilteredRecords,
    isFetchingInstantWin,
    instantWinError,
    activeTab,
    filters,
    page,
    rowsPerPage,
  } = useSelector((state) => state.luckyDrawListing);

  const [isPublishing, setIsPublishing] = useState(false);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [isDeactivateDialogOpen, setIsDeactivateDialogOpen] = useState(false);
  const [isActivateDialogOpen, setIsActivateDialogOpen] = useState(false);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);
  const [isEndJoinDialogOpen, setIsEndJoinDialogOpen] = useState(false);
  const [isEndLuckyDrawDialogOpen, setIsEndLuckyDrawDialogOpen] =
    useState(false);
  const [isLiveDrawSetupDialogOpen, setIsLiveDrawSetupDialogOpen] =
    useState(false);
  const [paginationOptions, setPaginationOptions] = useState([]);
  const [selectedLiveDrawUuid, setSelectedLiveDrawUuid] = useState(null);
  const [selectedPublishUuid, setSelectedPublishUuid] = useState(null);
  const [selectedUpdateLD, setSelectedUpdateLD] = useState(null);
  const [selectedBindingLD, setSelectedBindingLD] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [filterValues, setFilterValues] = useState({
    productsSelected: [],
    progressSelected: [],
    startPeriodFrom: null,
    startPeriodTo: null,
    endPeriodFrom: null,
    endPeriodTo: null,
  });

  // Get context for duplicate functionality
  const { setData } = useLuckyDraw();

  // Get current data based on active tab
  const currentData = activeTab === 0 ? traditionalRaffleList : instantWinList;
  const currentTotalRecords =
    activeTab === 0 ? traditionalRaffleTotalRecords : instantWinTotalRecords;
  const currentFilteredRecords =
    activeTab === 0
      ? traditionalRaffleFilteredRecords
      : instantWinFilteredRecords;
  const isFetching =
    activeTab === 0 ? isFetchingTraditionalRaffle : isFetchingInstantWin;
  const isError =
    activeTab === 0 ? !!traditionalRaffleError : !!instantWinError;

  // Sync context data for duplicate functionality
  useEffect(() => {
    if (currentData && currentData.length > 0) {
      setData(currentData);
    }
  }, [currentData, setData]);

  // Sync context data for duplicate functionality
  useEffect(() => {
    if (currentData && currentData.length > 0) {
      setData(currentData);
    }
  }, [currentData, setData]);

  const fetchData = useCallback(() => {
    const params = {
      limit: rowsPerPage,
      skip: page * rowsPerPage,
      search: filters.search,
      product: filters.product,
      startPeriodFrom: filters.startPeriodFrom,
      startPeriodTo: filters.startPeriodTo,
      endPeriodFrom: filters.endPeriodFrom,
      endPeriodTo: filters.endPeriodTo,
      progress: filters.progress,
    };

    if (activeTab === 0) {
      dispatch(fetchTraditionalRaffleList(params));
    } else {
      dispatch(fetchInstantWinList(params));
    }
  }, [dispatch, activeTab, page, rowsPerPage, filters]);

  useImperativeHandle(ref, () => ({
    reloadData() {
      fetchData();
    },
  }));

  useEffect(() => {
    dispatch(getAllProducts());
    return () => {
      dispatch(resetListingState());
    };
  }, [dispatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPaginationOptions(generatePaginationOptions(currentTotalRecords));
  }, [currentTotalRecords]);

  const handleTabChange = (event, newValue) => {
    dispatch(setListingActiveTab(newValue));
    if (onTabChange) {
      onTabChange(newValue);
    }
  };

  const handleFetchLuckyDrawList = () => {
    fetchData();
  };

  const handleChangePage = (event, newPage) => {
    dispatch(setListingPage(newPage));
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch(setListingRowsPerPage(parseInt(event.target.value, 10)));
  };

  const handleSearch = (values) => {
    setSearchValue(values.searchString);
    setFilterValues({
      productsSelected: values.product,
      progressSelected: values.progress,
      startPeriodFrom: values.startPeriodFrom,
      startPeriodTo: values.startPeriodTo,
      endPeriodFrom: values.endPeriodFrom,
      endPeriodTo: values.endPeriodTo,
    });
    dispatch(
      setListingFilters({
        search: values.searchString,
        product: values.product.map((item) => item.id),
        startPeriodFrom: values.startPeriodFrom || null,
        startPeriodTo: values.startPeriodTo || null,
        endPeriodFrom: values.endPeriodFrom || null,
        endPeriodTo: values.endPeriodTo || null,
        progress: values.progress.map((item) => item.id),
      })
    );
  };

  // eslint-disable-next-line no-unused-vars
  const handleClearFilters = () => {
    setSearchValue("");
    setFilterValues({
      productsSelected: [],
      progressSelected: [],
      startPeriodFrom: null,
      startPeriodTo: null,
      endPeriodFrom: null,
      endPeriodTo: null,
    });
    dispatch(clearListingFilters());
  };

  const handleStatusChange = ({ currentStatus, uuid }) => {
    // if (hasLuckyDrawActivateOrDeactivateAccessRight) {
    //   setSelectedUpdateUuid(uuid);
    //   if (currentStatus === "active") {
    //     setIsDeactivateDialogOpen(true);
    //   } else {
    //     setIsActivateDialogOpen(true);
    //   }
    // }
  };

  const handleStatusSubmit = (status) => {
    setIsStatusUpdating(true);
    luckyDrawV2Api
      .updateStatus({
        luckyDrawUuid: selectedUpdateLD.uuid,
        status: status ? "active" : "inactive",
      })
      .then(() => {
        fetchData();
      })
      .catch((error) => {
        dispatch(
          addAlert({
            severity: "error",
            message: getApiLang(lang, error.code),
          })
        );
      })
      .finally(() => {
        setIsStatusUpdating(false);
        setSelectedUpdateLD(null);
        setIsDeactivateDialogOpen(false);
        setIsActivateDialogOpen(false);
      });
  };

  const handleLiveDraw = (uuid, progress) => {
    setSelectedLiveDrawUuid(uuid);
    let ld = currentData.find((d) => d.uuid === uuid);
    if (ld && ld.draw_start_at) {
      history.push(`${ROOT}/live-draw/${uuid}`);
    } else {
      if (progress === progressStatus.published) {
        setIsLiveDrawSetupDialogOpen(true);
      } else {
        setIsConfirmationDialogOpen(true);
      }
    }
  };

  const handlePublished = (uuid, progress) => {
    if (progress === progressStatus.setProduct) {
      setSelectedPublishUuid(uuid);
      setIsConfirmationDialogOpen(true);
    }
  };

  const updateProgress = (isLiveDraw) => {
    setIsPublishing(true);
    let uuid = isLiveDraw ? selectedLiveDrawUuid : selectedPublishUuid;
    luckyDrawV2Api
      .publishLuckyDraw({ luckyDrawUuid: uuid })
      .then(() => {
        setIsConfirmationDialogOpen(false);
        if (isLiveDraw) {
          setIsLiveDrawSetupDialogOpen(true);
        }
        fetchData();
      })
      .catch((error) => {
        dispatch(
          addAlert({
            severity: "error",
            message: getApiLang(lang, error.code),
          })
        );
      })
      .finally(() => {
        setIsPublishing(false);
      });
  };

  const handleAddLuckyDraw = () => {
    history.push(LUCKY_DRAW_CREATE);
  };

  const handleAddInstantWin = () => {
    history.push(INSTANT_WIN_CREATE);
  };

  const handleBinding = (uuid) => {
    let selectedLuckyDraw = currentData.find((d) => d.uuid === uuid);

    //Dispatch products when the dialog is open (before open dropdown menu)
    dispatch(getAllProducts());
    setSelectedBindingLD(selectedLuckyDraw);
  };

  const handleDownloadWinner = (id) => {
    dispatch(showBackdrop());
    luckyDrawV2Api
      .exportWinnerList({ id: id, token: accessToken })
      .finally(() => {
        dispatch(hideBackdrop());
        return;
      });
  };

  const handleEnd = (uuid) => {
    let selected = currentData.find((d) => d.uuid === uuid);
    setSelectedUpdateLD(selected);
    setIsEndLuckyDrawDialogOpen(true);
  };

  const handleJoinClose = (uuid) => {
    let selected = currentData.find((d) => d.uuid === uuid);
    setSelectedUpdateLD(selected);
    setIsEndJoinDialogOpen(true);
  };

  const handleProceesEndJoin = () => {
    setIsStatusUpdating(true);
    luckyDrawV2Api
      .endLuckyDrawJoinPeriod({ uuid: selectedUpdateLD.uuid })
      .then(() => {
        fetchData();
      })
      .catch((error) => {
        dispatch(
          addAlert({
            severity: "error",
            message: getApiLang(lang, error.code),
          })
        );
      })
      .finally(() => {
        setSelectedUpdateLD(null);
        setIsEndJoinDialogOpen(false);
        setIsStatusUpdating(false);
      });
  };

  const handleProceesEndLuckyDraw = () => {
    setIsStatusUpdating(true);
    luckyDrawV2Api
      .endLuckyDraw({ uuid: selectedUpdateLD.uuid })
      .then(() => {
        fetchData();
      })
      .catch((error) => {
        dispatch(
          addAlert({
            severity: "error",
            message: getApiLang(lang, error.code),
          })
        );
      })
      .finally(() => {
        setSelectedUpdateLD(null);
        setIsEndLuckyDrawDialogOpen(false);
        setIsStatusUpdating(false);
      });
  };

  return (
    <>
      <LuckyDrawListingPanelComponent
        hasLuckyDrawEditAccessRight={hasLuckyDrawEditAccessRight}
        isFetching={isFetching}
        activeTab={activeTab}
        handleTabChange={handleTabChange}
        page={page}
        rowsPerPage={rowsPerPage}
        totalRecords={currentFilteredRecords}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        handleSearch={handleSearch}
        handleStatusChange={handleStatusChange}
        handleFetchLuckyDrawList={handleFetchLuckyDrawList}
        paginationOptions={paginationOptions}
        productDropdown={productDropdown}
        isFetchingProduct={isFetchingProduct}
        lang={lang}
        language={language}
        isError={isError}
        handleDownloadWinner={handleDownloadWinner}
        handleLiveDraw={handleLiveDraw}
        handleAddLuckyDraw={handleAddLuckyDraw}
        handleAddInstantWin={handleAddInstantWin}
        handleBinding={handleBinding}
        handlePublished={handlePublished}
        handleEnd={handleEnd}
        handleJoinClose={handleJoinClose}
        data={currentData}
        searchValue={searchValue}
        filterValues={filterValues}
      />
      <LuckyDrawBindingDialog
        isOpen={!!selectedBindingLD}
        handleClose={() => {
          setSelectedBindingLD(null);
        }}
        handleReload={() => fetchData()}
        luckyDraw={selectedBindingLD}
      />
      <LiveDrawSetupDialog
        isOpen={isLiveDrawSetupDialogOpen}
        handleClose={() => {
          setIsLiveDrawSetupDialogOpen(false);
          setSelectedLiveDrawUuid(null);
        }}
        uuid={selectedLiveDrawUuid}
      />
      <ConfirmationDialog
        isOpen={isDeactivateDialogOpen}
        handleClose={() => setIsDeactivateDialogOpen(false)}
        handleProceed={() => handleStatusSubmit(false)}
        isLoading={isStatusUpdating}
        type={"danger"}
        description={getLang(lang, "paragraph.DEACTIVATE_LD_QUESTION")}
        title={getLang(lang, "label.CONFIRMATION_NEEDED")}
      />
      <ConfirmationDialog
        isOpen={isActivateDialogOpen}
        handleClose={() => setIsActivateDialogOpen(false)}
        handleProceed={() => handleStatusSubmit(true)}
        isLoading={isStatusUpdating}
        description={getLang(lang, "paragraph.ACTIVATE_LD_QUESTION")}
        title={getLang(lang, "label.CONFIRMATION_NEEDED")}
      />
      <ConfirmationDialog
        isOpen={isConfirmationDialogOpen}
        handleClose={() => setIsConfirmationDialogOpen(false)}
        handleProceed={() => {
          let isLiveDraw = !!selectedLiveDrawUuid;
          updateProgress(isLiveDraw);
        }}
        type={"success"}
        remark={getLang(lang, "paragraph.PUBLISH_LD_REMARK")}
        description={getLang(lang, "paragraph.PLEASE_CONFIRM")}
        title={getLang(lang, "paragraph.PUBLISH_LD_QUESTION")}
        isLoading={isPublishing}
      />
      <GeneralDialog
        isOpen={isEndJoinDialogOpen}
        handleClose={() => setIsEndJoinDialogOpen(false)}
        handleProceed={() => {
          handleProceesEndJoin();
        }}
        type="danger"
        isLoading={isStatusUpdating}
        description={getLang(lang, "paragraph.END_LD_JOIN_PERIOD_QUESTION")}
        title={getLang(lang, "label.END_JOIN_PERIOD")}
        icon={<img src={AlertIcon} alt="alert" />}
      />
      <GeneralDialog
        isOpen={isEndLuckyDrawDialogOpen}
        handleClose={() => setIsEndLuckyDrawDialogOpen(false)}
        handleProceed={() => {
          handleProceesEndLuckyDraw();
        }}
        type="danger"
        isLoading={isStatusUpdating}
        description={
          selectedUpdateLD?.draw_start_at
            ? getLang(lang, "paragraph.END_LD_QUESTION")
            : getLang(lang, "paragraph.END_LD_LIVE_DRAW_WARNING_QUESTION")
        }
        title={getLang(lang, "label.END_LUCKY_DRAW")}
        icon={<img src={AlertIcon} alt="alert" />}
      />
    </>
  );
});

export default LuckyDrawListingPanelContainer;
